import React, { forwardRef } from "react";
import { Search } from "lucide-react";
import { useCityName } from "@/hooks/useCityName";
import { cn } from "@/lib/utils";
import type { City } from "@/components/forms/CityInputWithPortal";

export interface CityInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  onCitySelect?: (city: City) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
}

const CityInput = forwardRef<HTMLInputElement, CityInputProps>(
  (
    {
      onCitySelect,
      onChange,
      icon = <Search size={24} className="text-muted-foreground mr-2" />,
      className = "",
      inputClassName = "",
      dropdownClassName = "",
      placeholder = "Rechercher une ville",
      ...props
    },
    ref
  ) => {
    const {
      query,
      setQuery,
      cities,
      isOpen,
      isLoading,
      selectedIndex,
      containerRef,
      dropdownRef,
      handleKeyDown,
      selectCity,
    } = useCityName();

    const handleSelect = (city: City) => {
      selectCity(city);
      if (onCitySelect) {
        onCitySelect(city);
      }
      if (onChange) {
        const syntheticEvent = {
          target: { value: city.nom },
          currentTarget: { value: city.nom },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const handleCustomKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && cities.length > 0 && selectedIndex >= 0) {
        e.preventDefault();
        const selectedCity = cities[selectedIndex];
        handleSelect(selectedCity);
      } else {
        handleKeyDown(e);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className={cn("relative", className)} ref={containerRef}>
        <div className="flex items-center">
          {icon}
          <input
            ref={ref}
            type="text"
            placeholder={placeholder}
            className={cn(
              "w-full border-none focus:outline-none placeholder:text-muted-foreground text-foreground",
              inputClassName
            )}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleCustomKeyDown}
            {...props}
          />
          {isLoading && (
            <div className="absolute right-2">
              <div className="w-4 h-4 border-2 border-t-primary border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {isOpen && cities.length > 0 && (
          <div
            className={cn(
              "absolute top-full mt-2 w-full bg-white rounded-md shadow-lg z-10 max-h-60 overflow-y-auto",
              dropdownClassName
            )}
            ref={dropdownRef}
          >
            <ul className="py-1">
              {cities.map((city, index) => (
                <li
                  key={city.code}
                  className={cn(
                    "px-4 py-2 cursor-pointer hover:bg-gray-100",
                    index === selectedIndex ? "bg-gray-100" : ""
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(city);
                  }}
                >
                  {city.nom}{" "}
                  <span className="text-muted-foreground text-sm">
                    ({city.departement.nom})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
);

CityInput.displayName = "CityInput";

export { CityInput };
